import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { toast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api/client";
import { api } from "../../lib/api/endpoints";
import type { SyncOutcome } from "../../lib/api/models";
import { refreshAfterSync } from "../../lib/queries";
import { syncProblem, syncSummary } from "./summary";

/**
 * Starts a sync, waits for it, says what changed in a toast and refreshes what the screens show. `busy` is true
 * while one runs, so its button can be disabled.
 */
export function useSync() {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  // Set at once: `busy` only changes on the next render, so a second click in the same tick would pass it.
  const running = useRef(false);
  const run = async (start: () => Promise<SyncOutcome>) => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try {
      const outcome = await start();
      toast(syncSummary(outcome.counts), "success");
    } catch (e) {
      // Someone else's sync is not an error: the data arrives either way.
      toast(syncProblem(e), e instanceof ApiError && e.code === "sync_running" ? "info" : "error");
    } finally {
      // A failed refresh is not the sync's problem: the screens refetch on their own later.
      await refreshAfterSync(queryClient).catch(() => undefined);
      running.current = false;
      setBusy(false);
    }
  };
  return {
    busy,
    syncAll: () => run(() => api.gorelo.sync()),
    syncClient: (id: string) => run(() => api.clients.sync(id)),
    applyRemovals: () => run(() => api.gorelo.sync({ applyRemovals: true })),
  };
}
